import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateAddress, validateString } from '/imports/validators';
import { pluck } from '/imports/utils';
import { strings } from '/imports/strings';
import { GmapsAddress } from '/imports/gmaps';

export interface Org extends Doc {
  _id: string;
  nid: number;
  sid?: string;
  active?: boolean;
  name?: string;
  type?: string;
  phone?: string;
  address?: GmapsAddress;
  email?: string;
  website?: string;
  timezone?: string;
  avatar?: FileMeta;
  logo?: FileMeta;
  primaryColor?: string;
  secondaryColor?: string;
  billingAccessCode?: string;
  projectChannel?: 'affiliate' | 'dealer';
}

export type Model = Org;

type Refs = References<Model>;

export const Orgs = new Mongo.Collection<Org>('orgs');

export interface OrgBranding {
  orgName: string,
  avatar?: FileMeta,
  logo?: FileMeta,
  primaryColor?: string,
  secondaryColor?: string,
}
export const getOrgBranding = DeclareMethod<string, OrgBranding>('getOrgBranding');
export const updateMyOrg = DeclareMethod<Partial<Org>, string>('updateMyOrg');

export const modelClass: ModelClass<Model, Refs> = Object.freeze({
  collection: Orgs,
  editableFields: ['name', 'type', 'phone', 'address', 'address2', 'suite', 'city', 'state', 'country', 'zipcode', 'email',
    'timezone', 'avatar', 'logo', 'primaryColor', 'secondaryColor', 'billingAccessCode', 'projectChannel', 'website',
  ],
  listMethod: DeclareMethod<ListMethodArg<Org>, ListMethodResponse<Org, Refs>>('listOrgs'),
  getMethod: DeclareMethod<string, GetMethodResponse<Org, Refs>>('getOrg'),
  createMethod: DeclareMethod<Partial<Org>, string>('createOrg'),
  updateMethod: DeclareMethod<Partial<Org>, string>('updateOrg'),
  deleteMethod: DeclareMethod<string>('deleteOrg'),
  pathPrefix: '/orgs',
  asString: (document: Org) => document.name || document._id,
  modelName: strings.Organization(),
  pluralName: strings.Organizations(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: (org: Partial<Org>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, org, 'name', { required: true });
    validateString(errors, org, 'phone');
    validateAddress(errors, org, 'address');
    validateString(errors, org, 'timezone');
    return errors;
  },
});

export const getOrgProfile = DeclareMethod<string, Pick<Org, 'avatar' | 'logo' | 'name' | 'city' | 'state' | 'primaryColor' | 'secondaryColor'>>('getOrgProfile');

export const orgSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  return {
    $or: [
      { _id: search },
      { name: new RegExp(search, 'gi') },
    ]
  };
};

export const cleanOrg = (org: Partial<Org>) => {
  return pluck(org, [
    'nid',
    'active',
    'name',
    'type',
    'phone',
    'address',
    'suite',
    'email',
    'timezone',
    'avatar',
    'logo',
    'primaryColor',
    'secondaryColor',
    'billingAccessCode',
    'projectChannel',
    'website',
  ]);
};

export const cleanMyOrg = (org: Partial<Org>) => {
  return pluck(org, [
    'name',
    'phone',
    'address',
    'email',
    'timezone',
    'avatar',
    'logo',
    'primaryColor',
    'secondaryColor',
    'website',
  ]);
};

export const safeCleanOrg = (org: Partial<Org>) => {
  return pluck(org, modelClass.editableFields || []);
};

export const canListOrgs = (user?: Meteor.User | null) => {
  if (user?.isSuperUser) return true;
  return false;
};

export const orgName = (org: Org) => {
  return org.name || 'My Organization';
};

let _defaultOrg: Org;

export const defaultOrg = async () => {
  if (_defaultOrg) return _defaultOrg;
  _defaultOrg = await Orgs.findOneAsync('default') as Org;
  return _defaultOrg;
};